import { admissionsCopy as c } from "./admissions-copy";
import type { QueryClient } from "@tanstack/react-query";

import type {
  ApplicantDetail,
  ApplicantIs,
  ApplicantListItem,
  ApplicantProfile,
  ApplicationDocument,
} from "@/lib/api/applicants";
import type { IssuedInvitation } from "@/lib/api/invitations";
import type { RegistrarListReturnState } from "@/navigation/routes";

/**
 * History state an Admissions row carries into the file it opens: the list as
 * the reader left it, and the ids of that reading in its order, so the file can
 * step through the same queue the register showed. A direct arrival has none.
 */
export interface ApplicantQueueState extends RegistrarListReturnState {
  registrarQueue?: string[];
}

/** Where the file sits in the queue it was opened from, or null without one. */
export interface ApplicantQueuePlace {
  queue: string[];
  index: number;
  previousId: string | null;
  nextId: string | null;
}

export function applicantQueuePlace(state: unknown, applicantId: string): ApplicantQueuePlace | null {
  const queue = (state as ApplicantQueueState | null)?.registrarQueue;
  if (!Array.isArray(queue) || !queue.every((id) => typeof id === "string")) return null;
  const index = queue.indexOf(applicantId);
  if (index === -1) return null;
  return {
    queue,
    index,
    previousId: queue[index - 1] ?? null,
    nextId: queue[index + 1] ?? null,
  };
}

export function invitationOutcome(result: IssuedInvitation): string {
  switch (result.delivery_state) {
    case "sent": return `Invitation sent to ${result.delivery_email}.`;
    case "failed": return "Invitation created. Email delivery failed. Open the invitation to review recovery.";
    case "not_configured": return "Invitation created. Email delivery is not configured. Open the invitation for details.";
    case "ambiguous": return "Invitation created. Email delivery is uncertain. Open the invitation to reconcile before retrying.";
    case "pending": return "Invitation created. Email delivery is pending. Open the invitation for current progress.";
  }
}

export type IntakeDetailRow = { label: string; value: string; wide?: boolean };

/**
 * Who the file's contact details actually reach. The public form requires this
 * answer, so a null is a row that predates the question rather than an
 * applicant who declined it.
 *
 * It earns a place beside the address and the number because "Send invitation"
 * mails `applicant.email`, which is a parent's inbox whenever a parent applied
 * on the student's behalf. A registrar reading only the address cannot tell
 * those two cases apart.
 */
const APPLICANT_IS_LABEL: Record<ApplicantIs, string> = c.submittedByLabels;

/**
 * The base contact answers every tenant collects, unlike the tenant-configured
 * intake block below. These render unconditionally, and an unanswered optional
 * phone reads as c.notProvided rather than vanishing: the form always asks for
 * it, so its absence is a fact about this applicant, not about the tenant.
 */
export function contactDetailRows(applicant: ApplicantProfile): IntakeDetailRow[] {
  return [
    { label: c.contactFields.email, value: applicant.email },
    { label: c.contactFields.phone, value: applicant.phone?.trim() || c.notProvided },
    {
      label: c.contactFields.submittedBy,
      value: applicant.applicant_is
        ? APPLICANT_IS_LABEL[applicant.applicant_is]
        : c.notRecordedValue,
    },
  ];
}

/**
 * The tenant-configured intake answers this applicant actually submitted
 * (migration 054), in form order. Absent values are omitted rather than shown
 * as blanks: a tenant that does not collect a field, and a row that predates
 * the field, must not read as an unanswered question.
 */
export function intakeDetailRows(applicant: ApplicantProfile): IntakeDetailRow[] {
  const candidates: IntakeDetailRow[] = [
    { label: c.intakeLabels.date_of_birth, value: applicant.date_of_birth ?? "" },
    { label: c.intakeLabels.school_name, value: applicant.school_name ?? "" },
    { label: c.intakeLabels.grade_level, value: applicant.grade_level ?? "" },
    { label: c.intakeLabels.major, value: applicant.major ?? "" },
    { label: c.intakeLabels.mother_name, value: applicant.mother_name ?? "" },
    { label: c.intakeLabels.mother_phone, value: applicant.mother_phone ?? "" },
    { label: c.intakeLabels.mother_address, value: applicant.mother_address ?? "", wide: true },
    { label: c.intakeLabels.father_name, value: applicant.father_name ?? "" },
    { label: c.intakeLabels.father_phone, value: applicant.father_phone ?? "" },
    { label: c.intakeLabels.father_address, value: applicant.father_address ?? "", wide: true },
  ];
  return candidates.filter((row) => row.value.trim() !== "");
}

/** Mirrors the server roster rule: received and waived resolve a document. */
function areApplicantDocumentsComplete(documents: ApplicationDocument[]) {
  return documents.length > 0
    && documents.every((document) => document.status !== "pending");
}

/**
 * Writes one file's change into both reads that show it: the file itself and
 * the register row the list renders, so the way back reads the same truth.
 */
export function syncApplicantCaches(
  queryClient: QueryClient,
  applicantId: string,
  update: (current: ApplicantDetail) => ApplicantDetail,
) {
  let nextDetail: ApplicantDetail | undefined;
  queryClient.setQueryData<ApplicantDetail>(["applicant", applicantId], (current) => {
    if (!current) return current;
    nextDetail = update(current);
    return nextDetail;
  });

  if (!nextDetail) return;
  const detail = nextDetail;
  queryClient.setQueryData<ApplicantListItem[]>(["applicants"], (current) =>
    current?.map((applicant) => applicant.id === applicantId
      ? {
          ...applicant,
          name: detail.applicant.name,
          email: detail.applicant.email,
          phone: detail.applicant.phone,
          program_id: detail.applicant.program_id,
          program_name: detail.applicant.program_name,
          round: detail.applicant.round,
          stage: detail.applicant.stage,
          priority: detail.applicant.priority,
          applied_date: detail.applicant.applied_date,
          applicant_is: detail.applicant.applicant_is,
          converted_student_id: detail.applicant.converted_student_id,
          doc_total: detail.documents.length,
          doc_complete: areApplicantDocumentsComplete(detail.documents),
        }
      : applicant),
  );
}

export async function cancelApplicantReads(queryClient: QueryClient, applicantId: string) {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: ["applicants"] }),
    queryClient.cancelQueries({ queryKey: ["applicant", applicantId] }),
  ]);
}

export function revalidateApplicantReads(queryClient: QueryClient, applicantId: string) {
  void queryClient.invalidateQueries({ queryKey: ["applicants"] });
  void queryClient.invalidateQueries({ queryKey: ["applicant", applicantId] });
}
